import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  IconButton,
  Chip,
  Container,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AudioFile as AudioFileIcon,
} from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, getMultipartHeader } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";

const VoiceFileUpload = () => {
  const [formData, setFormData] = useState({
    promptCategory: "menu",
    fileName: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const promptCategories = [
    "welcome",
    "menu",
    "thanks",
    "noinput",
    "wronginput",
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Validate file type
      const fileExtension = file.name.toLowerCase().split(".").pop();
      if (fileExtension !== "wav") {
        setError("Only WAV files are supported");
        setSelectedFile(null);
        event.target.value = null;
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        setSelectedFile(null);
        event.target.value = null;
        return;
      }

      setSelectedFile(file);
      setError("");

      // Auto-fill fileName if empty
      if (!formData.fileName) {
        setFormData((prev) => ({
          ...prev,
          fileName: file.name.replace(".wav", ""),
        }));
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    // Reset file input
    const fileInput = document.getElementById("voice-file-input");
    if (fileInput) fileInput.value = null;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!selectedFile) {
      setError("Please select a WAV file");
      return;
    }

    setUploading(true);

    try {
      const uploadData = new FormData();
      uploadData.append("waveFile", selectedFile);
      uploadData.append("promptCategory", formData.promptCategory);
      uploadData.append("fileType", "wav");
      uploadData.append(
        "fileName",
        formData.fileName.trim() || selectedFile.name
      );
      const response = await axios.post(
        `${API_ENDPOINT}/multipart/upload-voice-file`,
        uploadData,
        getMultipartHeader()
      );

      setSuccess("Voice file uploaded successfully!");

      // Reset form
      setSelectedFile(null);
      setFormData({
        userId: "",
        promptCategory: "menu",
        fileName: "",
      });

      // Reset file input
      const fileInput = document.getElementById("voice-file-input");
      if (fileInput) fileInput.value = null;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload voice file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Paper elevation={0} sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Upload Voice File
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* File Upload */}
            <Box>
              <input
                accept=".wav,audio/wav,audio/wave,audio/x-wav"
                style={{ display: "none" }}
                id="voice-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="voice-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  Select WAV File
                </Button>
              </label>

              {selectedFile && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AudioFileIcon color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    disabled={uploading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Prompt Category */}
            <FormControl fullWidth>
              <InputLabel>Prompt Category</InputLabel>
              <Select
                name="promptCategory"
                value={formData.promptCategory}
                onChange={handleInputChange}
                label="Prompt Category"
                disabled={uploading}
              >
                {promptCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* File Name */}
            <TextField
              label="File Name (Optional)"
              name="fileName"
              value={formData.fileName}
              onChange={handleInputChange}
              fullWidth
              disabled={uploading}
              helperText="Leave blank to use original filename"
            />

            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {/* Success Alert */}
            {success && (
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}

            {/* Progress Bar */}
            {uploading && <LinearProgress />}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={uploading || !selectedFile}
              fullWidth
            >
              {uploading ? "Uploading..." : "Upload Voice File"}
            </Button>

            {/* Info */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label="Supported: WAV only"
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label="Max size: 10MB"
                size="small"
                variant="outlined"
                color="primary"
              />
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default VoiceFileUpload;
